<script>
export default {
  name: "Internships",
};
</script>

<script setup>
import InternshipSelectionModal from "./InternshipSelectionModal";
import FromLibrary from "./FromLibrary.vue";
import { computed, defineEmits, defineProps, ref, onMounted, unref } from "vue";
import { useVModel } from "@vueuse/core";
import { updateFieldToDecimals } from "@/services/utils";
import { useVuelidate } from "@vuelidate/core";
import { sessionStatus } from "@/components/program-manager/sessions/constants";
import { helpers, requiredIf } from "@vuelidate/validators";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import placementTypesService from "@/services/placementTypes";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import WYSIWYG from "@/components/forms/SharedComponents/WYSIWYG/WYSIWYG.vue";
import * as Sentry from "@sentry/vue";
import { cloneDeep } from "lodash";
import { MAX_DESCRIPTION_TEXTAREA_LIMIT } from "@/constants";

const placementTypesOptions = ref([]);

const props = defineProps({
  modelValue: {
    type: Object,
  },
  isSessionOwner: {
    type: Boolean,
  },
});
const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

const rules = {
  internship_fee: {
    required: helpers.withMessage(
      "Internship Fee is required to open enrollment",
      requiredIf(function () {
        return (
          unref(programSession)?.status_id === sessionStatus.openEnrollment &&
          !!unref(programSession)?.internship_filter_criteria
        );
      })
    ),
  },
};

const v$ = useVuelidate(rules, programSession, {
  $registerAs: "Internships",
  $lazy: true,
});

const updateFilters = (updateFilters) => {
  programSession.value.session_internships = [];
  programSession.value.internship_filter_criteria = updateFilters;
};

const updateSelectedInternships = (selectedInternships) => {
  programSession.value.internship_filter_criteria = {
    default_internship_id: undefined,
  };
  programSession.value.session_internships = selectedInternships;
};

const selectedInternshipIds = computed(() => {
  return programSession.value?.session_internships
    ? programSession.value?.session_internships?.map((i) => i.internship_id) ??
        []
    : [];
});

const modalOpen = ref(false);
const clearFilters = () => {
  programSession.value.internship_filter_criteria = undefined;
};

const updateDefaultInternshipId = (id) => {
  programSession.value.internship_filter_criteria.default_internship_id = id;
};

const openInternshipFilterModal = () => {
  modalOpen.value = true;
};

const internshipDuration = computed(() => {
  return Number(programSession?.value?.duration) ?? 0;
});
const fetchPlacementTypes = async () => {
  try {
    const placementsTypes = await placementTypesService.list();

    placementTypesOptions.value = (placementsTypes ?? []).map((item) => ({
      id: item.id,
      value: item.value,
    }));
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        troubleshooting_note:
          "Error fetching placement types on session internship cms.",
      },
    });
    console.error("Error fetching placement types:", error);
  }
};

const formatFilters = computed(() => {
  const newFilters = cloneDeep(programSession.value.internship_filter_criteria);
  if (Array.isArray(newFilters?.placement_type)) {
    newFilters.placement_type = newFilters.placement_type?.[0];
  }
  return newFilters;
});

onMounted(() => {
  fetchPlacementTypes();
});
</script>

<template>
  <ExpandingSection field-label="Internships" class="col-span-2">
    <template #content>
      <div class="grid grid-cols-2 gap-x-24 gap-y-4">
        <div class="col-span-2">
          <label class="common-label-text">
            Internships Description
          </label>
          <WYSIWYG
            data-testid="internships_description"
            v-model="programSession.internships_description"
            :character-limit="MAX_DESCRIPTION_TEXTAREA_LIMIT"
          />
        </div>
        <div class="flex justify-between items-center col-span-2">
          <div class="common-label-text">Internships</div>
          <div>
            <InternshipSelectionModal
              :internshipsOwnerId="props.modelValue.owner_entity_id"
              v-if="modalOpen"
              :open="modalOpen"
              :terms="props.modelValue.terms"
              :original-filters="formatFilters"
              @update-filters="updateFilters"
              @close-modal="() => (modalOpen = false)"
              @update-selected-internships="updateSelectedInternships"
              :academic-start-date="props.modelValue.academic_start_date"
              :internship-duration="internshipDuration"
              :placement-types-options="placementTypesOptions"
            />
            <PlusIcon
              data-testid="internship_modal_open"
              class="mr-2 text-teal-900 w-8 h-8 cursor-pointer"
              @click="openInternshipFilterModal"
            />
          </div>
        </div>
        <FromLibrary
          v-if="
            programSession.internship_filter_criteria ||
            selectedInternshipIds.length > 0
          "
          :filters="formatFilters"
          @edit-filters="openInternshipFilterModal"
          @clear-filters="clearFilters"
          @update-default-id="updateDefaultInternshipId"
          :academic-start-date="programSession.academic_start_date"
          :internship-duration="internshipDuration"
          :selected-internship-ids="selectedInternshipIds"
        />
        <BasicTextInput
          v-if="props.isSessionOwner"
          v-model="programSession.internship_fee"
          input-classes="h-10 w-full form-input"
          :vuelidate-instance="v$.internship_fee"
          type="number"
          step="0.01"
          min="0"
          outer-classes="col-span-2 flex flex-wrap dollar-input items-center mt-2"
          @blur="() => updateFieldToDecimals('internship_fee', programSession)"
        >
          <template #fieldLabel>
            <div class="common-label-text w-full">
              <span
                v-if="programSession.internship_filter_criteria"
                class="text-red-100 pr-2"
                >**</span
              >Internship Fee
            </div>
            <span class="common-label-text mr-2">$</span>
          </template>
        </BasicTextInput>
      </div>
    </template>
  </ExpandingSection>
</template>

<style scoped>
.dollar-input :deep(input) {
  width: calc(100% - 18px);
}
</style>
